// Texts of the keydetail area (GIOTHUB-185). English defines the keys.
import type { Dict } from "./dict";

const en = {
  "kd.aria": "Key detail",
  "kd.close": "Close the detail",
  "kd.proposal": "AI proposal",
  "kd.confidence": "· {level} confidence",
  "kd.conf.high": "high",
  "kd.conf.medium": "medium",
  "kd.conf.low": "low",
  "kd.state.proposed": "proposed",
  "kd.state.accepted": "accepted",
  "kd.state.edited": "edited",
  "kd.state.rejected": "rejected",
  "kd.noreason": "No reason given.",
  "kd.pending": "The assistant proposes a change",
  "kd.pending.edited": "You edited this key: taking the new proposal replaces your edit. Compare first, or edit the fields below instead.",
  "kd.adopt": "Take the new proposal",
  "kd.hintsref": "Every hint is explained in the {link}.",
  "kd.hintsref.link": "hints reference",
  "kd.class": "Class",
  "kd.type": "Type",
  "kd.unit": "Unit",
  "kd.nodata": "No-data value",
  "kd.eg": "e.g. {value}",
  "kd.min": "Min",
  "kd.max": "Max",
  "kd.decimals": "Decimals",
  "kd.decimals.hint": "shown",
  "kd.minmax.hint": "empty = none",
  "kd.role": "Role",
  "kd.nokind": "no kind",
  "kd.norole": "(no role)",
  "kd.qualifiers": "Qualifiers",
  "kd.qualifiers.hint": "name=value, comma-separated",
  "kd.label": "Label",
  "kd.description": "Description",
  "kd.meaning": "Meaning for the AI",
  "kd.meaning.hint": "what the value says about the real world, <= 300 characters",
  "kd.relates": "Relates to",
  "kd.relates.hint": "keys, comma-separated",
  "kd.caveats": "Caveats",
  "kd.param": "Parameter class",
  "kd.param.hint": "who may change this setting in a partner tenant; none = C0 (store warning)",
  "kd.param.none": "(none - counts as C0)",
  "kd.param.C0": "integrity: Gimasi only (broker, credentials, bands)",
  "kd.param.C1": "budget: Gimasi envelope, then the organisations",
  "kd.param.C2": "product: the organisation tree (limits, alarm delays)",
  "kd.param.C3": "operational: the operational dimension",
  "kd.onboarding": "Asked at onboarding",
  "kd.onboarding.hint": "The installer sets this value when the device is added (the default applies if left as it is).",
  "kd.confirm": "Confirm before a change",
  "kd.confirm.hint": "a warning shown before the value is changed, <= 300 characters; empty = none",
  "kd.confirm.ph": "Changing the use case restarts the measurement series.",
  "kd.reject": "Reject",
  "kd.saving": "Saving…",
  "kd.saveedit": "Save my edit",
  "kd.accept": "Accept proposal",
  "kd.geo": "Geolocation meaning",
  "kd.geo.takes": "takes {unit}",
  "kd.geo.hint": "what the field means for location",
  "kd.geo.none": "(none)",
  "kd.geo.suggested": "Suggested from the name - check before you use one:",
  "kd.geo.use": "Use {tag}",
  "kd.geo.group": "Position group",
  "kd.geo.group.hint": "only for a second position in the same message",
  "kd.geo.altref": "Altitude reference",
  "kd.geo.altref.default": "msl (default)",
  "kd.geo.gnbbits": "gNB id bits",
  "kd.geo.gnbbits.hint": "22-32, default 24",
  "kd.geo.rat": "Technology names",
  "kd.geo.rat.hint": "value=gsm|umts|lte|nbiot|nr, comma-separated",
  "kd.commands": "Commands",
  "kd.commands.intro": "What the platform may send to the device, as people see it: a label per command, a question to confirm destructive ones, and the arguments. Written into classification.yaml (commands) with the review; edit it there afterwards if needed.",
  "kd.commands.hidden": "hidden",
  "kd.commands.asks": "Asks: \"{question}\"",
} as const;

const de = {
  "kd.aria": "Schlüsseldetail",
  "kd.close": "Detail schliessen",
  "kd.proposal": "KI-Vorschlag",
  "kd.confidence": "· Konfidenz {level}",
  "kd.conf.high": "hoch",
  "kd.conf.medium": "mittel",
  "kd.conf.low": "niedrig",
  "kd.state.proposed": "vorgeschlagen",
  "kd.state.accepted": "angenommen",
  "kd.state.edited": "bearbeitet",
  "kd.state.rejected": "abgelehnt",
  "kd.noreason": "Keine Begründung angegeben.",
  "kd.pending": "Der Assistent schlägt eine Änderung vor",
  "kd.pending.edited": "Sie haben diesen Schlüssel bearbeitet: Der neue Vorschlag ersetzt Ihre Bearbeitung. Vergleichen Sie zuerst oder bearbeiten Sie stattdessen die Felder unten.",
  "kd.adopt": "Neuen Vorschlag übernehmen",
  "kd.hintsref": "Jeder Hinweis ist in der {link} erklärt.",
  "kd.hintsref.link": "Hinweisreferenz",
  "kd.class": "Klasse",
  "kd.type": "Typ",
  "kd.unit": "Einheit",
  "kd.nodata": "Wert für «keine Daten»",
  "kd.eg": "z. B. {value}",
  "kd.min": "Min.",
  "kd.max": "Max.",
  "kd.decimals": "Dezimalstellen",
  "kd.decimals.hint": "angezeigt",
  "kd.minmax.hint": "leer = keine",
  "kd.role": "Rolle",
  "kd.nokind": "keine Grösse",
  "kd.norole": "(keine Rolle)",
  "kd.qualifiers": "Qualifikatoren",
  "kd.qualifiers.hint": "name=wert, durch Kommas getrennt",
  "kd.label": "Bezeichnung",
  "kd.description": "Beschreibung",
  "kd.meaning": "Bedeutung für die KI",
  "kd.meaning.hint": "was der Wert über die reale Welt aussagt, <= 300 Zeichen",
  "kd.relates": "Bezieht sich auf",
  "kd.relates.hint": "Schlüssel, durch Kommas getrennt",
  "kd.caveats": "Vorbehalte",
  "kd.param": "Parameterklasse",
  "kd.param.hint": "wer diese Einstellung in einem Partner-Mandanten ändern darf; keine = C0 (Store-Warnung)",
  "kd.param.none": "(keine - gilt als C0)",
  "kd.param.C0": "Integrität: nur Gimasi (Broker, Zugangsdaten, Bänder)",
  "kd.param.C1": "Budget: Rahmen von Gimasi, dann die Organisationen",
  "kd.param.C2": "Produkt: der Organisationsbaum (Grenzwerte, Alarmverzögerungen)",
  "kd.param.C3": "betrieblich: die betriebliche Ebene",
  "kd.onboarding": "Beim Onboarding abgefragt",
  "kd.onboarding.hint": "Der Installateur setzt diesen Wert, wenn das Gerät hinzugefügt wird (sonst gilt der Standardwert).",
  "kd.confirm": "Vor einer Änderung bestätigen",
  "kd.confirm.hint": "eine Warnung vor der Änderung des Werts, <= 300 Zeichen; leer = keine",
  "kd.confirm.ph": "Eine Änderung des Anwendungsfalls startet die Messreihe neu.",
  "kd.reject": "Ablehnen",
  "kd.saving": "Wird gespeichert…",
  "kd.saveedit": "Meine Bearbeitung speichern",
  "kd.accept": "Vorschlag annehmen",
  "kd.geo": "Bedeutung für die Geolokalisierung",
  "kd.geo.takes": "erwartet {unit}",
  "kd.geo.hint": "was das Feld für die Ortung bedeutet",
  "kd.geo.none": "(keine)",
  "kd.geo.suggested": "Aus dem Namen vorgeschlagen - prüfen Sie vor der Verwendung:",
  "kd.geo.use": "{tag} verwenden",
  "kd.geo.group": "Positionsgruppe",
  "kd.geo.group.hint": "nur für eine zweite Position in derselben Nachricht",
  "kd.geo.altref": "Höhenbezug",
  "kd.geo.altref.default": "msl (Standard)",
  "kd.geo.gnbbits": "Bits der gNB-ID",
  "kd.geo.gnbbits.hint": "22-32, Standard 24",
  "kd.geo.rat": "Technologienamen",
  "kd.geo.rat.hint": "wert=gsm|umts|lte|nbiot|nr, durch Kommas getrennt",
  "kd.commands": "Befehle",
  "kd.commands.intro": "Was die Plattform an das Gerät senden darf, wie es die Benutzer sehen: eine Bezeichnung pro Befehl, eine Bestätigungsfrage für destruktive Befehle und die Argumente. Wird mit der Prüfung in classification.yaml (commands) geschrieben; danach bei Bedarf dort bearbeiten.",
  "kd.commands.hidden": "verborgen",
  "kd.commands.asks": "Fragt: «{question}»",
} satisfies Dict<typeof en>;

const it = {
  "kd.aria": "Dettaglio della chiave",
  "kd.close": "Chiudi il dettaglio",
  "kd.proposal": "Proposta dell'IA",
  "kd.confidence": "· affidabilità {level}",
  "kd.conf.high": "alta",
  "kd.conf.medium": "media",
  "kd.conf.low": "bassa",
  "kd.state.proposed": "proposta",
  "kd.state.accepted": "accettata",
  "kd.state.edited": "modificata",
  "kd.state.rejected": "rifiutata",
  "kd.noreason": "Nessuna motivazione indicata.",
  "kd.pending": "L'assistente propone una modifica",
  "kd.pending.edited": "Ha modificato questa chiave: accettando la nuova proposta la Sua modifica viene sostituita. Confronti prima, oppure modifichi i campi qui sotto.",
  "kd.adopt": "Prendi la nuova proposta",
  "kd.hintsref": "Ogni indicazione è spiegata nel {link}.",
  "kd.hintsref.link": "riferimento delle indicazioni",
  "kd.class": "Classe",
  "kd.type": "Tipo",
  "kd.unit": "Unità",
  "kd.nodata": "Valore «nessun dato»",
  "kd.eg": "p. es. {value}",
  "kd.min": "Min",
  "kd.max": "Max",
  "kd.decimals": "Decimali",
  "kd.decimals.hint": "mostrati",
  "kd.minmax.hint": "vuoto = nessuno",
  "kd.role": "Ruolo",
  "kd.nokind": "nessuna grandezza",
  "kd.norole": "(nessun ruolo)",
  "kd.qualifiers": "Qualificatori",
  "kd.qualifiers.hint": "nome=valore, separati da virgole",
  "kd.label": "Etichetta",
  "kd.description": "Descrizione",
  "kd.meaning": "Significato per l'IA",
  "kd.meaning.hint": "cosa dice il valore sul mondo reale, <= 300 caratteri",
  "kd.relates": "Correlato a",
  "kd.relates.hint": "chiavi, separate da virgole",
  "kd.caveats": "Avvertenze",
  "kd.param": "Classe del parametro",
  "kd.param.hint": "chi può modificare questa impostazione in un tenant partner; nessuna = C0 (avviso dello store)",
  "kd.param.none": "(nessuna - vale come C0)",
  "kd.param.C0": "integrità: solo Gimasi (broker, credenziali, bande)",
  "kd.param.C1": "budget: margine di Gimasi, poi le organizzazioni",
  "kd.param.C2": "prodotto: l'albero delle organizzazioni (limiti, ritardi degli allarmi)",
  "kd.param.C3": "operativo: la dimensione operativa",
  "kd.onboarding": "Richiesto all'onboarding",
  "kd.onboarding.hint": "L'installatore imposta questo valore quando il dispositivo viene aggiunto (se lo lascia invariato vale il valore predefinito).",
  "kd.confirm": "Conferma prima di una modifica",
  "kd.confirm.hint": "un avviso mostrato prima di modificare il valore, <= 300 caratteri; vuoto = nessuno",
  "kd.confirm.ph": "La modifica del caso d'uso fa ripartire la serie di misure.",
  "kd.reject": "Rifiuta",
  "kd.saving": "Salvataggio…",
  "kd.saveedit": "Salva la mia modifica",
  "kd.accept": "Accetta la proposta",
  "kd.geo": "Significato per la geolocalizzazione",
  "kd.geo.takes": "accetta {unit}",
  "kd.geo.hint": "cosa significa il campo per la localizzazione",
  "kd.geo.none": "(nessuno)",
  "kd.geo.suggested": "Suggerito dal nome - verifichi prima di usarlo:",
  "kd.geo.use": "Usa {tag}",
  "kd.geo.group": "Gruppo di posizione",
  "kd.geo.group.hint": "solo per una seconda posizione nello stesso messaggio",
  "kd.geo.altref": "Riferimento dell'altitudine",
  "kd.geo.altref.default": "msl (predefinito)",
  "kd.geo.gnbbits": "Bit dell'id gNB",
  "kd.geo.gnbbits.hint": "22-32, predefinito 24",
  "kd.geo.rat": "Nomi delle tecnologie",
  "kd.geo.rat.hint": "valore=gsm|umts|lte|nbiot|nr, separati da virgole",
  "kd.commands": "Comandi",
  "kd.commands.intro": "Cosa la piattaforma può inviare al dispositivo, come lo vedono le persone: un'etichetta per comando, una domanda di conferma per quelli distruttivi e gli argomenti. Scritto in classification.yaml (commands) con la revisione; se serve, lo modifichi lì in seguito.",
  "kd.commands.hidden": "nascosto",
  "kd.commands.asks": "Chiede: «{question}»",
} satisfies Dict<typeof en>;

const fr = {
  "kd.aria": "Détail de la clé",
  "kd.close": "Fermer le détail",
  "kd.proposal": "Proposition de l'IA",
  "kd.confidence": "· confiance {level}",
  "kd.conf.high": "élevée",
  "kd.conf.medium": "moyenne",
  "kd.conf.low": "faible",
  "kd.state.proposed": "proposée",
  "kd.state.accepted": "acceptée",
  "kd.state.edited": "modifiée",
  "kd.state.rejected": "rejetée",
  "kd.noreason": "Aucune raison indiquée.",
  "kd.pending": "L'assistant propose une modification",
  "kd.pending.edited": "Vous avez modifié cette clé : reprendre la nouvelle proposition remplace votre modification. Comparez d'abord, ou modifiez plutôt les champs ci-dessous.",
  "kd.adopt": "Reprendre la nouvelle proposition",
  "kd.hintsref": "Chaque indication est expliquée dans la {link}.",
  "kd.hintsref.link": "référence des indications",
  "kd.class": "Classe",
  "kd.type": "Type",
  "kd.unit": "Unité",
  "kd.nodata": "Valeur « aucune donnée »",
  "kd.eg": "p. ex. {value}",
  "kd.min": "Min",
  "kd.max": "Max",
  "kd.decimals": "Décimales",
  "kd.decimals.hint": "affichées",
  "kd.minmax.hint": "vide = aucune",
  "kd.role": "Rôle",
  "kd.nokind": "aucune grandeur",
  "kd.norole": "(aucun rôle)",
  "kd.qualifiers": "Qualificatifs",
  "kd.qualifiers.hint": "nom=valeur, séparés par des virgules",
  "kd.label": "Libellé",
  "kd.description": "Description",
  "kd.meaning": "Signification pour l'IA",
  "kd.meaning.hint": "ce que la valeur dit du monde réel, <= 300 caractères",
  "kd.relates": "Lié à",
  "kd.relates.hint": "clés, séparées par des virgules",
  "kd.caveats": "Mises en garde",
  "kd.param": "Classe de paramètre",
  "kd.param.hint": "qui peut modifier ce réglage dans un tenant partenaire ; aucune = C0 (avertissement du store)",
  "kd.param.none": "(aucune - compte comme C0)",
  "kd.param.C0": "intégrité : Gimasi uniquement (broker, identifiants, bandes)",
  "kd.param.C1": "budget : enveloppe de Gimasi, puis les organisations",
  "kd.param.C2": "produit : l'arbre des organisations (limites, délais d'alarme)",
  "kd.param.C3": "opérationnel : la dimension opérationnelle",
  "kd.onboarding": "Demandé à l'onboarding",
  "kd.onboarding.hint": "L'installateur définit cette valeur à l'ajout de l'appareil (la valeur par défaut s'applique s'il ne la change pas).",
  "kd.confirm": "Confirmer avant une modification",
  "kd.confirm.hint": "un avertissement affiché avant de modifier la valeur, <= 300 caractères ; vide = aucun",
  "kd.confirm.ph": "Changer le cas d'usage redémarre la série de mesures.",
  "kd.reject": "Rejeter",
  "kd.saving": "Enregistrement…",
  "kd.saveedit": "Enregistrer ma modification",
  "kd.accept": "Accepter la proposition",
  "kd.geo": "Signification pour la géolocalisation",
  "kd.geo.takes": "attend {unit}",
  "kd.geo.hint": "ce que le champ signifie pour la localisation",
  "kd.geo.none": "(aucune)",
  "kd.geo.suggested": "Suggéré d'après le nom - vérifiez avant d'en utiliser une :",
  "kd.geo.use": "Utiliser {tag}",
  "kd.geo.group": "Groupe de position",
  "kd.geo.group.hint": "seulement pour une deuxième position dans le même message",
  "kd.geo.altref": "Référence d'altitude",
  "kd.geo.altref.default": "msl (par défaut)",
  "kd.geo.gnbbits": "Bits de l'id gNB",
  "kd.geo.gnbbits.hint": "22-32, par défaut 24",
  "kd.geo.rat": "Noms des technologies",
  "kd.geo.rat.hint": "valeur=gsm|umts|lte|nbiot|nr, séparés par des virgules",
  "kd.commands": "Commandes",
  "kd.commands.intro": "Ce que la plateforme peut envoyer à l'appareil, tel que les personnes le voient : un libellé par commande, une question de confirmation pour les commandes destructives, et les arguments. Écrit dans classification.yaml (commands) avec la revue ; modifiez-le ensuite à cet endroit si nécessaire.",
  "kd.commands.hidden": "masquée",
  "kd.commands.asks": "Demande : « {question} »",
} satisfies Dict<typeof en>;

export default { en, de, it, fr };
